"use client";

import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { notFound } from "next/navigation";
import Navbar from "@/components/ui/Navbar";
import Footer from "@/components/sections/Footer";
import { motion } from "framer-motion";
import { 
    Server,
    Cpu,
    Activity,
    Shield
} from "lucide-react";

// Standard animation variants
const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] }
};

const MobileMockup = ({ imageSrc, altText, delay, wrapperClassName = "" }: { imageSrc: string; altText: string; delay: number; wrapperClassName?: string }) => (
    <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full flex justify-center ${wrapperClassName}`}
    >
        <div className="relative w-full max-w-[280px] sm:max-w-[320px] aspect-[9/19] group">
            {/* Buttons */}
            <div className="absolute left-[-3px] top-[90px] w-[3px] h-[16px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute left-[-3px] top-[120px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute left-[-3px] top-[164px] w-[3px] h-[32px] bg-gradient-to-r from-[#d1d1d6] to-[#a8a8b0] rounded-l-md transition-colors" />
            <div className="absolute right-[-3px] top-[140px] w-[3px] h-[48px] bg-gradient-to-l from-[#d1d1d6] to-[#a8a8b0] rounded-r-md transition-colors" />
            
            {/* Phone Frame Outer (Silver Edge) */}
            <div className="w-full h-full rounded-[48px] bg-gradient-to-br from-[#e5e5ea] via-[#b0b0b5] to-[#e5e5ea] p-[3px] shadow-[0_15px_35px_rgba(0,0,0,0.1)] transition-all duration-500 group-hover:shadow-[0_20px_45px_rgba(68,170,82,0.15)]">
                
                {/* Phone Frame Inner (Black Bezel) */}
                <div className="w-full h-full rounded-[45px] bg-black p-[4px] sm:p-[5px] relative overflow-hidden">
                    
                    {/* Top Speaker Grill */}
                    <div className="absolute top-[1px] sm:top-[1.5px] left-1/2 -translate-x-1/2 w-[30px] sm:w-[40px] h-[2px] bg-[#222] rounded-full z-20" />
                    
                    {/* Screen Image */}
                    <div className="w-full h-full rounded-[41px] sm:rounded-[40px] overflow-hidden bg-white relative">
                        <img 
                            src={imageSrc} 
                            alt={altText} 
                            className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

const LaptopMockup = ({ imageSrc }: { imageSrc: string }) => (
    <motion.div 
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-[700px] mx-auto relative group perspective-[1200px]"
    >
        {/* Screen Top Half */}
        <div className="relative rounded-t-[24px] bg-[#0A0A0C] p-[8px] pb-0 border-t border-x border-[#1C1C1F] shadow-2xl z-10 origin-bottom transition-transform duration-700 ease-out group-hover:rotate-x-[2deg]">
            {/* Screen Inner Bezel */}
            <div className="w-full bg-[#1e1e1e] rounded-t-[16px] overflow-hidden relative aspect-[16/10] border border-[#2d2d30]">
                {/* Camera Hole */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-black rounded-full z-20" />
                
                {/* Screenshot */}
                <img 
                    src={imageSrc} 
                    alt="Desktop Mockup" 
                    className="w-full h-full object-cover object-top"
                />
                
                {/* Glare */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 pointer-events-none" />
            </div>
            {/* Bottom Bezel (Chin) */}
            <div className="h-7 w-full bg-black rounded-b-[8px] relative flex items-center justify-center border-b border-zinc-900">
                <span className="text-[6px] text-zinc-500 font-bold tracking-widest font-sans">KITEBE</span>
            </div>
        </div>

        {/* Base / Keyboard Deck */}
        <div className="relative h-[12px] w-[105%] -ml-[2.5%] bg-gradient-to-b from-[#e5e5ea] to-[#b0b0b5] rounded-b-[16px] shadow-lg z-0 flex justify-center border-t border-white/20">
            {/* Thumb Cutout */}
            <div className="w-24 h-1.5 bg-[#a0a0a5] rounded-b-md" />
        </div>
        
        {/* Shadow */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[80%] h-12 bg-black/10 blur-2xl z-[-1]" />
    </motion.div>
);

export default function CTLPage() {
    const [visibility, setVisibility] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function checkVisibility() {
            try {
                if (db) {
                    const docRef = doc(db, "projects", "ctl");
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setVisibility(data.visibility || "private");
                    } else {
                        // Fallback to public if the document does not exist yet (e.g. before first seeding)
                        setVisibility("public");
                    }
                } else {
                    setVisibility("public");
                }
            } catch (error) {
                console.error("Error checking CTL project visibility:", error);
                setVisibility("public");
            } finally {
                setLoading(false);
            }
        }
        checkVisibility();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-zinc-200 border-t-[#44AA52] rounded-full" />
            </div>
        );
    }

    if (visibility !== "public") {
        notFound();
    }

    return (
        <main data-header-theme="dark" className="min-h-screen bg-white text-[#444] selection:bg-[#44AA52] selection:text-white font-sans antialiased overflow-x-hidden">
            <Navbar />

            {/* ───────────────── HERO SECTION ───────────────── */}
            <section data-header-theme="dark" className="relative pt-36 pb-20 px-6 md:px-12 lg:px-24 xl:px-32 border-b border-[#EAEAEA] bg-[radial-gradient(#44aa5205_1px,transparent_1px)] [background-size:32px_32px]">
                {/* Background Ambient Glows */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#44aa52]/3 rounded-full blur-[140px] pointer-events-none" />
                <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-[#44aa52]/2 rounded-full blur-[120px] pointer-events-none" />

                <div className="container mx-auto z-10 w-full">
                    {/* Top Case Study Metadata Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pb-16 border-b border-[#EAEAEA] text-xs font-mono text-zinc-500">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[#44AA52] uppercase tracking-wider font-semibold">CLIENT / TYPE</span>
                            <span className="text-zinc-800 font-medium">CTL Infocom Private Limited</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[#44AA52] uppercase tracking-wider font-semibold">EXPERT AREAS</span>
                            <span className="text-zinc-800 font-medium">IT Infrastructure, IT Engineering, Cloud Services</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[#44AA52] uppercase tracking-wider font-semibold">ESTABLISHED</span>
                            <span className="text-zinc-800 font-medium">1994 (ISO 9001-2015 Certified)</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[#44AA52] uppercase tracking-wider font-semibold">WEBSITE</span>
                            <a href="https://ctl-website-d3664.web.app" target="_blank" rel="noopener noreferrer" className="text-zinc-800 hover:text-[#44AA52] transition-colors font-medium underline underline-offset-4">ctl-website-d3664.web.app</a>
                        </div>
                    </div>

                    {/* Main Content & Laptop */}
                    <div className="pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                        
                        {/* Left: Text */}
                        <div className="lg:col-span-6 space-y-8">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#44aa52]/10 text-[#44aa52] text-xs font-semibold tracking-wider uppercase">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#44aa52] animate-pulse" />
                                Est. 1994 / ISO 9001-2015
                            </div>
                            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.15]">
                                EFFICIENCY &amp; EXCELLENCE <br />
                                ARE THE SAME. <br />
                                <span className="font-light italic text-[#44AA52]">THE FUTURE LOOKS HYBRID.</span>
                            </h1>

                            <p className="text-zinc-650 text-sm md:text-base leading-relaxed max-w-2xl font-light pt-2">
                                We support your pursuit for technology driven growth with powerfully designed IT infrastructure. Since 1994, we have underlined every project with quality and excellence thus enriching our experience and growing our expertise in the area of Information Technology Management, IT Infrastructure Solution and IT Engineering.
                            </p>
                        </div>
                        
                        {/* Right: Laptop Mockup */}
                        <div className="lg:col-span-6 relative hidden lg:block">
                            <LaptopMockup imageSrc="/images/ctl_desktop_full.png" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────── 01 PROFILE & CAPABILITIES ───────────────── */}
            <section data-header-theme="dark" className="py-24 px-6 md:px-12 lg:px-24 bg-[#F8FAF8] border-b border-[#EAEAEA]">
                <div className="container mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-stretch">
                        
                        {/* Section Tag */}
                        <div className="lg:col-span-3 flex flex-col justify-between">
                            <div>
                                <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">01 / OVERVIEW</span>
                                <h2 className="text-3xl font-light text-[#1A1A1A] leading-tight">Corporate Intentions <br />&amp; Foundations</h2>
                            </div>
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-400">
                                SYSTEM INTENTIONS &amp; HISTORIES
                            </div>
                        </div>

                        {/* Intention Card */}
                        <div className="lg:col-span-4 bg-white border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 shadow-sm">
                            <div>
                                <span className="text-[10px] font-mono text-[#44AA52] uppercase tracking-widest font-semibold">// COLLABORATIVE INTENTIONS</span>
                                <p className="text-zinc-650 text-sm leading-relaxed font-light mt-4">
                                    We are using technology to transform the world by designing efficiently and responsibly for businesses that want to enhance their customer experience and expand their brand offerings. We meet you halfway with innovation and deliver researched, customised solutions as per your business requirements.
                                </p>
                            </div>
                            <div className="h-[1px] bg-[#EAEAEA]" />
                            <div className="text-[10px] font-mono text-zinc-450">
                                Commitment: Designing efficiently and responsibly
                            </div>
                        </div>

                        {/* Capability Card */}
                        <div className="lg:col-span-5 bg-white border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#44aa52]/3 rounded-full blur-2xl pointer-events-none" />
                            
                            <div>
                                <span className="text-[10px] font-mono text-[#44AA52] uppercase tracking-widest font-semibold">// COMPREHENSIVE EXPERTISE</span>
                                <p className="text-zinc-650 text-sm leading-relaxed font-light mt-4">
                                    CTL instituted in 1994, is an ISO 9001 -2015 Certified Organisation. With initial operations in the telecom industry for MTNL in Mumbai, it made its foray into the Information Technology segment. Since 2004, CTL has successfully rendered influential collaborations for Data Center solutions, Electronic Security surveillance, Building Management Solutions, Audio and Video solutions, Artificial Intelligence, and IoT.
                                </p>
                            </div>
                            <div className="h-[1px] bg-[#EAEAEA]" />
                            <div className="text-[10px] font-mono text-zinc-450 flex justify-between">
                                <span>Certifications: CDCP, CDCS, CDCE</span>
                                <span className="text-[#44AA52] font-semibold">ISO 9001-2015</span>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ───────────────── 02 LAYOUT SYSTEM GRID ───────────────── */}
            <section data-header-theme="dark" className="py-28 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EAEAEA]">
                <div className="container mx-auto">
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">02 / OPERATIONAL SPECIFICATION</span>
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                            Infrastructure Grid &amp; Project Footprint
                        </h2>
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-xl">
                            A breakdown of our legacy system footprint. Our engineering models support major projects across airports, banking networks, and high-capacity data centers.
                        </p>
                    </div>

                    {/* The Grid Blueprint Card */}
                    <div className="w-full bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-6 md:p-8 lg:p-12 relative overflow-hidden shadow-sm">
                        
                        {/* Ruler Markings */}
                        <div className="absolute top-0 left-12 right-12 h-6 border-b border-[#EBEBEB] flex justify-between items-center text-[9px] font-mono text-zinc-400 select-none pointer-events-none">
                            <span>0px</span>
                            <span>305px</span>
                            <span>626px</span>
                            <span>947px</span>
                            <span>1268px</span>
                            <span>1440px</span>
                        </div>
                        <div className="absolute left-0 top-12 bottom-12 w-6 border-r border-[#EBEBEB] flex flex-col justify-between items-center text-[9px] font-mono text-zinc-400 py-2 select-none pointer-events-none">
                            <span>Y:0</span>
                            <span>Y:200</span>
                            <span>Y:400</span>
                            <span>Y:600</span>
                        </div>

                        {/* Interactive Blueprint Mockup */}
                        <div className="pt-8 grid grid-cols-1 md:grid-cols-12 gap-6 relative z-10">
                            
                            {/* Left block representation */}
                            <div className="md:col-span-4 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    System Scale
                                </div>
                                <div className="space-y-4">
                                    <span className="text-[10px] font-mono text-[#44AA52] tracking-widest block font-semibold">// CORE STATISTICS</span>
                                    <h3 className="text-2xl font-light text-[#1A1A1A] italic">Global Partnerships</h3>
                                    <p className="text-zinc-650 text-[11px] leading-relaxed font-light">
                                        We have established a successful alliance with 2000+ clients and empower 5 Million users with technology driven growth.
                                    </p>
                                </div>
                                <div className="text-[8px] font-mono text-zinc-450 font-medium">
                                    25+ Years of Experience
                                </div>
                            </div>

                            {/* Middle block representation */}
                            <div className="md:col-span-5 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    Project Deployments
                                </div>
                                <div className="space-y-4">
                                    <div className="h-28 rounded bg-white border border-[#E5ECE5] flex flex-col items-center justify-center font-mono text-[9px] text-[#44AA52] relative overflow-hidden p-4 space-y-2 shadow-xs">
                                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#44aa5208_1px,transparent_1px),linear-gradient(to_bottom,#44aa5208_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
                                        <div className="text-[10px] font-bold text-[#44AA52]">CIAL DATA CENTER BUILD</div>
                                        <div className="text-[8px] text-zinc-400">Design, Construction &amp; Commissioning</div>
                                    </div>
                                    <div className="flex justify-between items-center text-[10px] font-mono">
                                        <span className="text-zinc-550">Federal Bank: 800 Branches</span>
                                        <span className="text-[#44AA52] font-semibold">SIB: 700 Branches</span>
                                    </div>
                                </div>
                                <div className="text-[8px] font-mono text-zinc-400">
                                    Locations: Mumbai, Bengaluru, Kochi
                                </div>
                            </div>

                            {/* Right block representation */}
                            <div className="md:col-span-3 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    Consultancies
                                </div>
                                <div className="space-y-4">
                                    <div className="p-3 bg-white border border-[#E5ECE5] rounded text-center shadow-xs">
                                        <span className="text-[10px] font-mono text-[#44AA52] block font-bold">Kannur Airport</span>
                                        <span className="text-[8px] font-mono text-zinc-400 block uppercase">IT &amp; ELV Consultant</span>
                                    </div>
                                    <div className="p-3 bg-white border border-[#E5ECE5] rounded text-center shadow-xs">
                                        <span className="text-[10px] font-mono text-zinc-800 block font-medium">HAL Bangalore</span>
                                        <span className="text-[8px] font-mono text-zinc-450 block uppercase">Data Center Build</span>
                                    </div>
                                </div>
                                <div className="w-full py-2 border border-[#E5ECE5] text-[#44AA52] bg-[#44aa52]/5 font-mono text-[9px] text-center uppercase tracking-wider font-semibold rounded">
                                    ESTABLISHED 1994
                                </div>
                            </div>

                        </div>

                        {/* Extra Layout spec details underneath */}
                        <div className="mt-8 border-t border-[#EAEAEA] pt-6 flex flex-wrap justify-between items-center text-[10px] font-mono text-zinc-400 gap-4">
                            <div>TECHNOLOGY DRIVEN INFRASTRUCTURE: MULTI-OFFICE PAN-INDIA OPERATIONS</div>
                            <div className="flex gap-4">
                                <span>OFFICES: MUMBAI</span>
                                <span>BENGALURU</span>
                                <span>KOCHI</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────── 03 TYPOGRAPHY & COLORS ───────────────── */}
            <section data-header-theme="dark" className="py-24 px-6 md:px-12 lg:px-24 bg-[#F8FAF8] border-b border-[#EAEAEA]">
                <div className="container mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        
                        {/* Sticky left panel */}
                        <div className="lg:col-span-4 flex flex-col justify-between">
                            <div>
                                <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">03 / DESIGN SYSTEM</span>
                                <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">Typography <br />&amp; Colors</h2>
                                <p className="text-zinc-500 text-xs mt-4 font-light leading-relaxed max-w-sm">
                                    We align visual hierarchy with absolute technical precision. We use a clean, geometric sans-serif (Poppins) to create readable technical documentation and corporate materials.
                                </p>
                            </div>
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-400">
                                SYSTEM DEFINITION
                            </div>
                        </div>

                        {/* Right Content */}
                        <div className="lg:col-span-8 space-y-12">
                            
                            {/* Font Stack Box */}
                            <div className="border border-[#E5ECE5] bg-white rounded-2xl p-8 relative overflow-hidden shadow-sm">
                                <span className="text-[9px] font-mono text-zinc-450 uppercase block mb-6 font-semibold">TYPOGRAPHY STACK</span>
                                
                                <div className="space-y-4">
                                    <span className="text-[10px] font-mono text-[#44AA52] uppercase block font-semibold">Poppins Sans-Serif</span>
                                    <div className="text-6xl md:text-7xl font-light text-[#1A1A1A] tracking-tight">
                                        AaBbCc123
                                    </div>
                                    <p className="text-zinc-650 text-[11px] leading-relaxed font-light max-w-xl">
                                        Used for high-level editorial headers, statement quotes, section titles, and technical solution descriptions. Built for perfect balance and modern corporate clarity.
                                    </p>
                                </div>
                            </div>

                            {/* Color Swatches Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                
                                {/* Swatch 1 */}
                                <div className="bg-white border border-[#E5ECE5] rounded-xl p-6 flex flex-col justify-between h-48 shadow-sm">
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-mono text-zinc-400">HEX / #FFFFFF</span>
                                        <div className="w-5 h-5 rounded-full bg-[#FFFFFF] border border-[#E5ECE5]" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-[#1A1A1A]">Pure Corporate White</h3>
                                        <p className="text-zinc-500 text-[10px] leading-relaxed font-light mt-1.5">
                                            The main background canvas, conveying structure, order, and high-quality corporate transparency.
                                        </p>
                                    </div>
                                </div>

                                {/* Swatch 2 */}
                                <div className="bg-white border border-[#E5ECE5] rounded-xl p-6 flex flex-col justify-between h-48 shadow-sm">
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-mono text-zinc-400">HEX / #44AA52</span>
                                        <div className="w-5 h-5 rounded-full bg-[#44AA52]" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-[#1A1A1A]">Evergreen Green</h3>
                                        <p className="text-zinc-500 text-[10px] leading-relaxed font-light mt-1.5">
                                            The signature brand color representing green technology solutions and secure MTNL/HCL enterprise origins.
                                        </p>
                                    </div>
                                </div>

                                {/* Swatch 3 */}
                                <div className="bg-white border border-[#E5ECE5] rounded-xl p-6 flex flex-col justify-between h-48 shadow-sm">
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-mono text-zinc-400">HEX / #6B6C66</span>
                                        <div className="w-5 h-5 rounded-full bg-[#6B6C66]" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-[#1A1A1A]">Charcoal Grey</h3>
                                        <p className="text-zinc-500 text-[10px] leading-relaxed font-light mt-1.5">
                                            A sophisticated, low-fatigue slate grey used for extensive bodies of text and details.
                                        </p>
                                    </div>
                                </div>

                            </div>

                        </div>

                    </div>
                </div>
            </section>
            
            {/* ───────────────── 04 FULL PAGE SCREENSHOT ───────────────── */}
            <section data-header-theme="dark" className="py-28 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EAEAEA]">
                <div className="container mx-auto">

                    {/* Label */}
                    <div className="mb-6 flex justify-between items-center">
                        <span className="text-[11px] font-mono text-zinc-500 tracking-wider">Desktop Viewport <span className="text-[#44AA52] font-semibold">[1280px]</span></span>
                        <span className="text-[10px] font-mono text-zinc-400">Actual Website Screenshot</span>
                    </div>

                    {/* Desktop Screenshot */}
                    <div className="w-full max-w-5xl mx-auto rounded-2xl overflow-hidden border border-[#E5ECE5] bg-white p-2 shadow-[0_30px_70px_rgba(0,0,0,0.08)]">
                        {/* Chrome Window Browser UI */}
                        <div className="w-full h-10 bg-zinc-50 border-b border-[#E5ECE5] rounded-t-xl flex items-center px-4 justify-between">
                            <div className="flex gap-1.5 items-center">
                                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                            </div>
                            <div className="h-6 bg-white px-12 rounded-md text-[10px] font-mono text-zinc-500 flex items-center border border-[#E5ECE5] gap-2 select-none">
                                <span className="text-zinc-300">https://</span>ctl-website-d3664.web.app
                            </div>
                            <div className="w-16" /> {/* Spacer */}
                        </div>

                        {/* Full Page View */}
                        <div className="relative w-full bg-[#FAFBF9]">
                            <img 
                                src="/images/ctl_desktop_full.png" 
                                alt="CTL Infocom Live Website Screenshot" 
                                className="w-full h-auto block rounded-b-xl"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────────── MOBILE EXPERIENCE SHOWCASE ───────────────── */}
            <section data-header-theme="dark" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F8FAF8] border-b border-[#EAEAEA] relative overflow-hidden">
                {/* Background Ambient Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#44aa52]/2 rounded-full blur-[140px] pointer-events-none" />
                
                <div className="container mx-auto">
                    {/* Section Header */}
                    <div className="max-w-3xl mb-16 md:mb-24">
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">04 / RESPONSIVE SHOWCASE</span>
                        <h2 className="text-4xl md:text-5xl font-light text-[#1A1A1A] leading-tight">
                            Bespoke Mobile Interface
                        </h2>
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-xl">
                            We translated complex data configurations, system metrics, and structured client testimonials into mobile-responsive layouts, keeping pixel alignment absolute.
                        </p>
                    </div>

                    {/* Staggered Phone Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 items-start">
                        
                        {/* Column 1: Left */}
                        <div className="flex flex-col items-center w-full">
                            <MobileMockup imageSrc="/images/ctl_mobile_1.png" altText="CTL Mobile Screen 1" delay={0} />
                            <MobileMockup imageSrc="/images/ctl_mobile_2.png" altText="CTL Mobile Screen 4" delay={0.1} wrapperClassName="mt-12 md:mt-[224px] lg:mt-[320px]" />
                        </div>

                        {/* Column 2: Middle */}
                        <div className="flex flex-col items-center w-full">
                            <MobileMockup imageSrc="/images/ctl_mobile_2.png" altText="CTL Mobile Screen 2" delay={0.15} />
                            <MobileMockup imageSrc="/images/ctl_mobile_3.png" altText="CTL Mobile Screen 5" delay={0.25} wrapperClassName="mt-12 md:mt-[320px] lg:mt-[448px]" />
                        </div>

                        {/* Column 3: Right */}
                        <div className="flex flex-col items-center w-full md:pt-32 lg:pt-64">
                            <MobileMockup imageSrc="/images/ctl_mobile_3.png" altText="CTL Mobile Screen 3" delay={0.3} />
                            <MobileMockup imageSrc="/images/ctl_mobile_1.png" altText="CTL Mobile Screen 6" delay={0.4} wrapperClassName="mt-12 md:mt-[288px] lg:mt-[320px]" />
                        </div>

                    </div>
                </div>
            </section>

            {/* ───────────────── 05 DESIGN & BRAND VALUES ───────────────── */}
            <section data-header-theme="dark" className="py-24 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EAEAEA]">
                <div className="container mx-auto">
                    
                    {/* Section tag */}
                    <div className="max-w-2xl mb-16">
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">05 / CORE VALUES</span>
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">Design &amp; Brand Values</h2>
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-lg">
                            We mapped the website architectural details across four primary pillars, directly referencing CTL's commitment to quality assurance and system expertise.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        
                        {/* Value 1 */}
                        <div className="bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#44AA52]/30 transition-all duration-500 shadow-xs">
                            <div className="space-y-4">
                                <div className="p-3 bg-[#44AA52]/10 border border-[#44AA52]/20 rounded-xl w-fit text-[#44AA52]">
                                    <Cpu className="w-5 h-5" />
                                </div>
                                <h3 className="text-xl font-medium text-[#1A1A1A]">QUALITY ASSURANCE</h3>
                                <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                    We institute ISO 9001 - 2015 certified standards, ensuring every design and installation is quality assured and result oriented.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-zinc-450 font-medium">// QUALITY ASSURANCE</span>
                        </div>

                        {/* Value 2 */}
                        <div className="bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#44AA52]/30 transition-all duration-500 shadow-xs">
                            <div className="space-y-4">
                                <div className="p-3 bg-[#44AA52]/10 border border-[#44AA52]/20 rounded-xl w-fit text-[#44AA52]">
                                    <Server className="w-5 h-5" />
                                </div>
                                <h3 className="text-xl font-medium text-[#1A1A1A]">EXPERIENCE</h3>
                                <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                    Rendering legacy collaborations since 1994, our 25+ years of experience establishes solid credibility in IT infrastructure.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-zinc-450 font-medium">// ESTABLISHED LEGACY</span>
                        </div>

                        {/* Value 3 */}
                        <div className="bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#44AA52]/30 transition-all duration-500 shadow-xs">
                            <div className="space-y-4">
                                <div className="p-3 bg-[#44AA52]/10 border border-[#44AA52]/20 rounded-xl w-fit text-[#44AA52]">
                                    <Activity className="w-5 h-5" />
                                </div>
                                <h3 className="text-xl font-medium text-[#1A1A1A]">GLOBAL STANDARDS</h3>
                                <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                    Certified by major OEMs and global standards of data centre certifications (CDCP, CDCS, CDCE) to support client-centric needs.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-zinc-450 font-medium">// GLOBAL STANDARDS</span>
                        </div>

                        {/* Value 4 */}
                        <div className="bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#44AA52]/30 transition-all duration-500 shadow-xs">
                            <div className="space-y-4">
                                <div className="p-3 bg-[#44AA52]/10 border border-[#44AA52]/20 rounded-xl w-fit text-[#44AA52]">
                                    <Shield className="w-5 h-5" />
                                </div>
                                <h3 className="text-xl font-medium text-[#1A1A1A]">CUSTOMISATION</h3>
                                <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                    Delivering highly researched, customised technological solutions designed specifically around your business operations.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-zinc-450 font-medium">// CUSTOMISED EXPERTISE</span>
                        </div>

                    </div>

                </div>
            </section>

            {/* ───────────────── FOOTER STATEMENT BANNER ───────────────── */}
            <section data-header-theme="dark" className="py-32 px-6 md:px-12 lg:px-24 bg-[#F8FAF8] text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(#44aa5203_1px,transparent_1px)] [background-size:32px_32px] pointer-events-none" />
                
                <div className="container mx-auto z-10 max-w-4xl relative">
                    <motion.div {...fadeInUp} className="space-y-8">
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-zinc-450 leading-tight select-none">
                            WE BUILD SEAMLESS TECHNOLOGICAL SOLUTIONS <br />
                            THAT ARE <span className="font-medium italic text-[#44AA52]">QUALITY ASSURED &amp; RESULT ORIENTED</span>
                        </h2>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
